// src/Components/Masters/Complaint/Dashboard/TktOvrTimeGraph.js
import React, { useEffect, useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  Label,
} from "recharts";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  useTheme,
  LinearProgress,
} from "@mui/material";
import { Refresh, TrendingUp } from "@mui/icons-material";
import { format, parseISO, startOfDay, endOfDay } from "date-fns";
import { toast } from "react-toastify";
import axiosInstance from "../../../../lib/axios";

/* ------------------------------------------------------------------ */
/*  STATUS → KEY, LABEL, COLOUR                                        */
/* ------------------------------------------------------------------ */
const STATUS = {
  O: { key: "unassigned", label: "Unassigned",   base: "#e53935" },
  I: { key: "inProgress", label: "In Progress", base: "#fb8c00" },
  R: { key: "resolved",   label: "Resolved",    base: "#ff7043" },
  C: { key: "closed",     label: "Closed",      base: "#43a047" },
};

const GRADIENT_OFFSET = 0.3;

const TktOvrTimeGraph = ({
  globalProperty,
  selectedCategory,
  selectedWarden,
  fromDate,
  toDate,
}) => {
  const theme = useTheme();

  const [raw, setRaw] = useState([]);
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [peak, setPeak] = useState(null);
  const [range, setRange] = useState({ start: null, end: null });

  /* ------------------------------------------------------------------ */
  /*  1. FETCH ALL TICKETS                                              */
  /* ------------------------------------------------------------------ */
  const fetchTickets = async () => {
    if (!globalProperty || !selectedCategory?.Id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        PageNumber: 1,
        PageSize: 9999,
        PropId: globalProperty,
        Id: selectedCategory.Id,
        WardenId: selectedWarden?.Id || 1,
        ServiceProvId: 1,
        SearchText: "",
        SearchFrom: fromDate,
        SearchTo: toDate,
        Flag: "TicketDash",
      };

      const {
        data: { Status, Data },
      } = await axiosInstance.post(
        "TrnTicketing/GetFilterTicketDashBoard",
        payload
      );

      if (Status !== 0 || !Array.isArray(Data) || Data.length === 0) {
       // toast.info("No tickets in the selected period");
        setRaw([]);
        return;
      }

      const start = startOfDay(new Date(fromDate));
      const end = endOfDay(new Date(toDate));

      const filtered = Data.filter((t) => {
        const d = parseISO(t.TktDate);
        return d >= start && d <= end;
      });

      setRaw(filtered);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tickets");
      setRaw([]);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  2. GROUP → CHART DATA (memoised)                                  */
  /* ------------------------------------------------------------------ */
  const processed = useMemo(() => {
    if (!raw.length) {
      return { chart: [], peak: null, range: { start: null, end: null } };
    }

    const groups = raw.reduce((acc, t) => {
      const day = format(parseISO(t.TktDate), "MMM dd");
      const st = STATUS[t.TicketSt] || STATUS.O;

      if (!acc[day]) {
        acc[day] = {
          date: day,
          unassigned: 0,
          inProgress: 0,
          resolved: 0,
          closed: 0,
        };
      }
      acc[day][st.key] += 1;
      return acc;
    }, {});

    const sorted = Object.values(groups).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const peakDay = sorted.reduce((max, cur) => {
      const curTot =
        cur.unassigned + cur.inProgress + cur.resolved + cur.closed;
      const maxTot =
        max.unassigned + max.inProgress + max.resolved + max.closed;
      return curTot > maxTot ? cur : max;
    }, sorted[0]);

    const dates = raw.map((t) => parseISO(t.TktDate));
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));

    return {
      chart: sorted,
      peak: peakDay?.date || null,
      range: { start: min, end: max },
    };
  }, [raw]);

  useEffect(() => {
    setChart(processed.chart);
    setPeak(processed.peak);
    setRange(processed.range);
  }, [processed]);

  /* ------------------------------------------------------------------ */
  /*  3. FETCH on prop change                                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    fetchTickets();
  }, [globalProperty, selectedCategory, selectedWarden, fromDate, toDate]);

  /* ------------------------------------------------------------------ */
  /*  4. CUSTOM TOOLTIP                                                 */
  /* ------------------------------------------------------------------ */
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const totals = payload.reduce((sum, p) => sum + p.value, 0);

    return (
      <Paper
        elevation={6}
        sx={{
          p: 2,
          borderRadius: 2,
          background: theme.palette.background.paper,
          borderLeft: `5px solid ${theme.palette.primary.main}`,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        {payload.map((p) => (
          <Box
            key={p.dataKey}
            sx={{ display: "flex", alignItems: "center", my: 0.5 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: p.color,
                borderRadius: "50%",
                mr: 1,
              }}
            />
            <Typography variant="body2">
              {p.name}: <strong>{p.value}</strong>
            </Typography>
          </Box>
        ))}
        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" fontWeight="bold">
            Total: <strong>{totals}</strong> tickets
          </Typography>
        </Box>
      </Paper>
    );
  };

  /* ------------------------------------------------------------------ */
  /*  5. RENDER                                                         */
  /* ------------------------------------------------------------------ */
  const hasData = chart.length > 0;
  const rangeText =
    range.start && range.end
      ? `${format(range.start, "MMM dd")} – ${format(range.end, "MMM dd, yyyy")}`
      : "No data";

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 4,
        boxShadow: 6,
        bgcolor: "background.paper",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Resolution Trend
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {rangeText}
          </Typography>
        </Box>

        <IconButton onClick={fetchTickets} color="primary" size="small">
          <Refresh />
        </IconButton>
      </Box>

      {/* SINGLE LEGEND ROW – OUTSIDE, ABOVE CHART */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2.5,
          flexWrap: "wrap",
          mt: 1,
          mb: 2,
        }}
      >
        {Object.values(STATUS).map((s) => {
          const total = chart.reduce((sum, day) => sum + (day[s.key] || 0), 0);
          return (
            <Box
              key={s.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: s.base,
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: s.base,
                  fontWeight: 600,
                }}
              >
                {s.label}: <strong>{total}</strong>
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Loading / Empty */}
      {loading ? (
        <LinearProgress color="primary" />
      ) : !hasData ? (
        <Typography align="center" color="textSecondary" py={8}>
          No tickets found for the selected period
        </Typography>
      ) : (
        <>
          {/* Chart */}
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chart}
                margin={{ top: 10, right: 30, left: 0, bottom: 80 }}
              >
                {/* Gradient definitions */}
                {Object.values(STATUS).map((s) => (
                  <defs key={s.key}>
                    <linearGradient id={`gradient-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset={0} stopColor={s.base} stopOpacity={0.9} />
                      <stop offset={GRADIENT_OFFSET} stopColor={s.base} stopOpacity={0.6} />
                      <stop offset={1} stopColor={s.base} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                ))}

                <CartesianGrid strokeDasharray="4 4" strokeOpacity={0.3} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                >
                  <Label value="Date" offset={-20} position="insideBottom" />
                </XAxis>
                <YAxis tick={{ fontSize: 12 }}>
                  <Label
                    value="Number of Tickets"
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: "middle" }}
                  />
                </YAxis>

                {/* Peak line */}
                {peak && (
                  <ReferenceLine
                    x={peak}
                    stroke={theme.palette.warning.main}
                    strokeDasharray="6 6"
                    strokeWidth={2}
                    label={{
                      value: "PEAK",
                      position: "top",
                      fill: theme.palette.warning.main,
                      fontWeight: "bold",
                      fontSize: 13,
                      dy: -8,
                    }}
                  />
                )}

                <Tooltip content={<CustomTooltip />} />

                {/* REMOVED <Legend /> – using custom chips above */}

                {/* Stacked Areas */}
                {Object.values(STATUS).map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stackId="stack"
                    stroke={s.base}
                    strokeWidth={2}
                    fill={`url(#gradient-${s.key})`}
                    name={s.label}
                    animationDuration={800}
                  />
                ))}

                <Brush
                  dataKey="date"
                  height={30}
                  stroke={theme.palette.primary.main}
                  travellerWidth={12}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>

          {/* Peak Badge */}
          {peak && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: theme.palette.warning.light,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderLeft: `5px solid ${theme.palette.warning.main}`,
              }}
            >
              <TrendingUp color="warning" />
              <Typography variant="body2" fontWeight="bold">
                Peak on <strong>{peak}</strong>:{" "}
                {(() => {
                  const d = chart.find((x) => x.date === peak);
                  return d
                    ? `${d.unassigned + d.inProgress + d.resolved + d.closed} tickets`
                    : "";
                })()}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default TktOvrTimeGraph;